/**
 * Is this page running inside the mobile app's editor WebView?
 *
 * The app opens the web editors with `embed=1` on the URL and has always
 * documented that flag as "hides its own chrome" — but nothing here ever read
 * it. The visible cost was two carts: adding a card or a calendar opened the
 * WEB cart drawer inside the WebView, and then the app opened its own, so the
 * customer answered the same question twice.
 *
 * Read from the URL rather than sniffing the user agent: the app states what it
 * is, and a WebView is otherwise indistinguishable from a browser that happens
 * to look like one.
 *
 * Remembered for the rest of the WebView's session: the flag is only on the
 * URL the app opens, and the customer walks on from there — povečave opens on
 * /wall-art?embed=1 and adds to cart from /canvas/<id>, whose URL no longer
 * carries it. Reading the URL alone opened the web drawer there again.
 * sessionStorage belongs to this one WebView, so a browser tab never inherits it.
 */
const SESSION_KEY = 'fd-embedded-app';

export function isEmbeddedInApp(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('embed') === '1') {
      remember();
      return true;
    }
  } catch {
    return false;
  }
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function remember() {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage blocked: this page still knows from its URL; a later page will not.
  }
}

// Record the flag as soon as the first page loads, before any client-side
// navigation drops it from the URL. This module is imported by the cart drawer
// provider, which every shop page mounts.
isEmbeddedInApp();

/**
 * Tell the app an item reached the order.
 *
 * The app's WebView listens for exactly this message and closes the editor onto
 * its own cart. Sending it is what lets the web stop opening a cart of its own.
 *
 * Silent when there is no bridge: on the open web this is simply not the app,
 * and the caller falls back to the drawer.
 */
export function notifyAppItemAdded(): boolean {
  if (typeof window === 'undefined') return false;
  const bridge = (window as { ReactNativeWebView?: { postMessage: (m: string) => void } })
    .ReactNativeWebView;
  if (!bridge) return false;
  try {
    bridge.postMessage(JSON.stringify({ type: 'item-added' }));
    return true;
  } catch {
    return false;
  }
}

type AppBridge = { postMessage: (m: string) => void };

/**
 * Can the app open a link in the phone's own browser? The app announces it by
 * injecting `window.__fdApp = { openExternal: true }`; app versions from before
 * that do not, and must not be shown a button that does nothing.
 */
export function appCanOpenBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as { __fdApp?: { openExternal?: boolean }; ReactNativeWebView?: AppBridge };
  return w.__fdApp?.openExternal === true && !!w.ReactNativeWebView;
}

/** Ask the app to open `url` in the phone's browser (Safari / Chrome). */
export function openInPhoneBrowser(url: string): boolean {
  if (typeof window === "undefined") return false;
  const bridge = (window as { ReactNativeWebView?: AppBridge }).ReactNativeWebView;
  if (!bridge) return false;
  try {
    bridge.postMessage(JSON.stringify({ type: "open-external", url }));
    return true;
  } catch {
    return false;
  }
}
